import type { SupStoredTxSettings } from "@/types";
import React, { Fragment, useCallback, useEffect, useState } from "react";

import { SupButton, SupDialog, SupDialogBody, SupDialogFooter, SupDialogHeader, SupTab, SupTabs, SupTabContext, SupText, SupLoader } from "@repo/core-ui";

import useValidateTxSettings from "@/hooks/tx-flow/settings/useValidateTxSettings";
import { useStoredData, useStoredDataDispatch } from "@/store/Context";
import { defaultTxSettings } from "@/store/data";
import Web3TxSettingsTabRoutes from "@/components/Web3/TxSettings/Tab/Routes";
import Web3TxSettingsTabRefunds from "@/components/Web3/TxSettings/Tab/Refunds";

interface Web3TxSettingsModalProps {
  open: boolean;
  setOpen: (a: boolean) => void;
  defaultTab?: "routes" | "refunds";
}

export default function Web3TxSettingsModal({ open, setOpen, defaultTab = "routes" }: Web3TxSettingsModalProps) {
  const storedData = useStoredData();
  const storedDispatch = useStoredDataDispatch();

  const [settings, setSettings] = useState<SupStoredTxSettings>(defaultTxSettings);

  const { isValid, isLoading } = useValidateTxSettings(settings);

  // Single-time hook
  useEffect(() => {
    setSettings(storedData.txSettings);
  }, [storedData.txSettings]);

  const onClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const onSaveSettings = useCallback(() => {
    storedDispatch({ type: "setTxSettings", txSettings: settings });
    onClose();
  }, [onClose, settings, storedDispatch]);

  return (
    <SupDialog className="max-w-screen-[80vw] max-h-[90vh] lg:max-w-80" open={open} onClose={onClose}>
      <SupDialogHeader onClose={onClose}>
        <SupText variant="subtitle1" weight="bold">
          TRANSACTION SETTINGS
        </SupText>
      </SupDialogHeader>
      <SupDialogBody>
        <SupTabContext value={defaultTab}>
          <Fragment>
            <SupTabs className="w-full justify-stretch">
              {["routes", "refunds"].map(tab => (
                <SupTab className="flex w-1/2 justify-center text-center" key={`tx-settings-tab-${tab}`} label={tab.toUpperCase()} value={tab} />
              ))}
            </SupTabs>
            <Web3TxSettingsTabRoutes setSettings={setSettings} settings={settings} />
            <Web3TxSettingsTabRefunds setSettings={setSettings} settings={settings} />
          </Fragment>
        </SupTabContext>
      </SupDialogBody>
      <SupDialogFooter className="justify-stretch">
        <SupButton color="primary" fluid onClick={onSaveSettings} disabled={!isValid}>
          {isLoading ? <SupLoader isSmall /> : "Save settings"}
        </SupButton>
      </SupDialogFooter>
    </SupDialog>
  );
}
